import { fetchRule, toRuleSet } from './recurrence-utils';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import moment from 'moment';
import { Options, RRule, RRuleSet } from "rrule";
import 'moment-timezone';
import { DateTime, IANAZone } from 'luxon';

const MINUTE_IN_MS = 60 * 1000;

// Generic offset shifter used by both "restore" and "adjust" paths.
const shiftByOffsetDiff = (date: Date, fromOffset: number | undefined, toOffset: number | undefined): Date => {
    if (fromOffset === undefined || toOffset === undefined || fromOffset === toOffset) {
        return date;
    }
    return new Date(date.getTime() + (toOffset - fromOffset) * MINUTE_IN_MS);
};

// Align an input date to the rule's original offset so `rule.after` walks the expected occurrence sequence.
const restoreToRRuleTime = (inputDate: Date, offsetRrule: number | undefined): Date => {
    return shiftByOffsetDiff(inputDate, inputDate.getTimezoneOffset(), offsetRrule);
};

// After `rule.after`, move the occurrence back to the current runtime offset to preserve local wall-clock time.
const adjustForDSTChange = (nextUTC: Date, offsetRrule: number | undefined): Date => {
    return shiftByOffsetDiff(nextUTC, offsetRrule, nextUTC.getTimezoneOffset());
};

// Convert a floating TZID date (wall-clock in tzid) into a real UTC instant.
const convertFloatingDateToUTC = (floatingDate: Date, tzid: string): Date => {
    const zonedDate = moment.tz({
        year: floatingDate.getUTCFullYear(),
        month: floatingDate.getUTCMonth(),
        date: floatingDate.getUTCDate(),
        hour: floatingDate.getUTCHours(),
        minute: floatingDate.getUTCMinutes(),
        second: floatingDate.getUTCSeconds(),
        millisecond: floatingDate.getUTCMilliseconds(),
    }, tzid);

    return zonedDate.utc().toDate();
};

// Normalize rule input so all downstream calculations are UTC-based and deterministic.
const getNormalizedRule = (rule: RRule, dueDate: moment.Moment): RRule => {
    const tzid = rule?.origOptions?.tzid || rule?.options?.tzid;
    const hasExplicitDtstart = !!rule?.origOptions?.dtstart;

    // Already explicit UTC-style rule; no normalization needed.
    if (!tzid && hasExplicitDtstart) {
        return rule;
    }

    const normalizedOptions: any = {
        ...rule.origOptions,
        dtstart: rule.origOptions.dtstart || dueDate.toDate(),
    };

    if (tzid) {
        // Strip floating TZID semantics by converting relevant boundaries to UTC.
        normalizedOptions.dtstart = convertFloatingDateToUTC(normalizedOptions.dtstart, tzid);
        if (normalizedOptions.until) {
            normalizedOptions.until = convertFloatingDateToUTC(normalizedOptions.until, tzid);
        }
        delete normalizedOptions.tzid;
    }

    return new RRule(normalizedOptions);
};

// Compute next occurrence from a reference date with full DST-safe normalization/adjustment.
// `excludedRawMillis` holds EXDATE instants in the rule's own (raw) space; occurrences
// matching an EXDATE are skipped (BE#648 round-2). An empty set => byte-identical to before.
const getNextAdjustedOccurrence = (
    rule: RRule,
    fromDate: Date,
    offsetRrule: number | undefined,
    excludedRawMillis: Set<number> = new Set(),
): Date | null => {
    let cursor: Date | null = restoreToRRuleTime(fromDate, offsetRrule);
    // Bounded skip loop: advance past EXDATE-d occurrences until a kept one (or exhaustion).
    for (let i = 0; i < 512; i++) {
        const next: Date | null = rule.after(cursor);
        if (!next) return null;
        if (!excludedRawMillis.has(next.getTime())) {
            return adjustForDSTChange(next, offsetRrule);
        }
        cursor = next;
    }
    return null;
};

// ===========================================================================
// BE#648 — tz-aware (Option B) expansion engine. RUNTIME-TZ-INDEPENDENT.
//
// Instead of shifting occurrences by the RUNTIME machine's getTimezoneOffset()
// (the ±1h DST drift for any customer whose zone != the server's), we:
//   1. expand the rule as FLOATING/naive — rrule floating uses UTC methods, so
//      BYHOUR=h yields occurrences whose getUTCHours() === h regardless of the
//      process TZ; then
//   2. map EACH occurrence's (Y,M,D,h,m,s) wall-clock -> the true UTC instant in
//      the caller-supplied IANA zone via luxon (installed, 3.6.1 — no new dep).
//
// The zone is passed IN by the caller (threaded from workspaces.timezone); it is
// never read from the environment. This generalises the dtstart-only
// `convertFloatingDateToUTC` to every occurrence and drops the offset-based
// `adjustForDSTChange` on the tz path.
// ===========================================================================

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface RecurrenceExpandQuery {
    /** Real-instant lower bound. */
    after?: Date;
    /** Real-instant upper bound. */
    before?: Date;
    /** Include occurrences that fall exactly on `after`/`before`. */
    inc?: boolean;
    /** Cap the number of returned instants (earliest first). */
    count?: number;
}

// Number of days ahead to expand when only `after` (no `before`) is given.
const DEFAULT_HORIZON_DAYS = 366 * 2;

const DAY_IN_MS = 24 * 60 * MINUTE_IN_MS;

interface WallClock {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond?: number;
}

/**
 * Wall-clock fields in `timezone` -> UTC instant, independent of the process clock.
 *
 * `DateTime.fromObject` seeds its offset guess from the zone's offset at "now", so an
 * AMBIGUOUS fall-back time resolved to a different instant depending on whether "now"
 * was before or after the transition (#538). Here the two candidate offsets are taken
 * from a day either side of the wall-clock itself: when both are valid (ambiguous) the
 * earlier instant, i.e. the pre-transition offset, wins; a nonexistent spring-forward
 * time is left to luxon, which advances it into the post-transition offset.
 */
const wallClockToInstant = (wall: WallClock, timezone: string): Date => {
    const zone = IANAZone.create(timezone);
    const localMs = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second, wall.millisecond ?? 0);
    const candidates = new Set<number>();
    for (const probe of [localMs - DAY_IN_MS, localMs + DAY_IN_MS]) {
        const instantMs = localMs - zone.offset(probe) * MINUTE_IN_MS;
        if (localMs - zone.offset(instantMs) * MINUTE_IN_MS === instantMs) candidates.add(instantMs);
    }
    if (candidates.size > 0) return new Date(Math.min(...candidates));
    return DateTime.fromObject(wall, { zone: timezone }).toUTC().toJSDate();
};

/**
 * Map a single FLOATING occurrence (wall-clock stored in the Date's UTC fields)
 * to the true UTC instant it represents in `timezone`. Deterministic across DST:
 * for an ambiguous fall-back local time luxon resolves to the PRE-transition
 * offset; for a nonexistent spring-forward local time it advances into the
 * post-transition offset. Never throws on either.
 */
export const floatingOccurrenceToInstant = (floating: Date, timezone: string): Date => {
    return wallClockToInstant(
        {
            year: floating.getUTCFullYear(),
            month: floating.getUTCMonth() + 1, // luxon months are 1-based
            day: floating.getUTCDate(),
            hour: floating.getUTCHours(),
            minute: floating.getUTCMinutes(),
            second: floating.getUTCSeconds(),
            millisecond: floating.getUTCMilliseconds(),
        },
        timezone,
    );
};

// Real instant -> the FLOATING wall-clock Date (wall-clock stored in UTC fields)
// for `timezone`, offset by `marginMs` to pad the floating search window.
const instantToFloatingWallClock = (instant: Date, timezone: string, marginMs = 0): Date => {
    const dt = DateTime.fromJSDate(instant).setZone(timezone);
    const base = Date.UTC(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second, dt.millisecond);
    return new Date(base + marginMs);
};

// Rebuild the parsed rule as a FLOATING rule (drop TZID) preserving
// FREQ/INTERVAL/BYDAY/COUNT and the wall-clock dtstart. rrule stores a TZID
// dtstart's wall-clock in its UTC fields, so it is already a valid floating
// dtstart. UNTIL is DROPPED here (set to null): per RFC 5545 it is a true UTC
// instant, so it must be compared against the mapped TRUE occurrence instants,
// NOT floatified into wall-clock space — the caller applies it after mapping.
const toFloatingRule = (parsed: RRule): RRule => {
    const options: Partial<Options> = { ...parsed.origOptions };
    options.tzid = null;
    options.until = null;
    return new RRule(options);
};

// The rule's UNTIL as a true UTC instant (RFC 5545), or undefined when absent.
const getUntilInstant = (parsed: RRule): Date | undefined => parsed.origOptions.until ?? undefined;

// Parse EXDATE lines from the raw ruleset string into a Set of excluded TRUE
// instants (ms). Handles all three forms: `EXDATE;TZID=Zone:...` (wall-clock in
// Zone), `EXDATE:...Z` (already a true instant), and a naive `EXDATE:...` (treated
// as wall-clock in `timezone`). This is the exclusion basis for the tz path.
const parseExcludedTrueInstants = (rruleString: string, timezone: string): Set<number> => {
    const excluded = new Set<number>();
    for (const rawLine of rruleString.split(/\r?\n/)) {
        const line = rawLine.trim();
        if (!/^EXDATE/i.test(line)) continue;
        const colon = line.indexOf(':');
        if (colon === -1) continue;
        const prefix = line.slice(0, colon);
        const values = line.slice(colon + 1);
        const tzidMatch = prefix.match(/TZID=([^;:]+)/i);
        const lineTzid = tzidMatch ? tzidMatch[1] : undefined;
        for (const token of values.split(',')) {
            const match = token.trim().match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z)?$/);
            if (!match) continue;
            const year = Number(match[1]);
            const month = Number(match[2]);
            const day = Number(match[3]);
            const hour = Number(match[4]);
            const minute = Number(match[5]);
            const second = Number(match[6]);
            const isUtc = match[7] === 'Z';
            const instant = isUtc
                ? new Date(Date.UTC(year, month - 1, day, hour, minute, second))
                : wallClockToInstant({ year, month, day, hour, minute, second }, lineTzid ?? timezone);
            excluded.add(instant.getTime());
        }
    }
    return excluded;
};

/**
 * Runtime-TZ-independent recurrence expansion (BE#648 Option B).
 *
 * When `timezone` is a truthy IANA zone, occurrences hold their in-tz wall-clock
 * across DST transitions. When `timezone` is falsy the rule is expanded NATIVELY
 * (absolute/UTC instants, unchanged) — this is the backward-compatible path for
 * un-migrated (no-TZID) series.
 */
export const expandRecurrenceInstants = (
    rruleString: string,
    timezone: string | undefined,
    query: RecurrenceExpandQuery = {},
): Date[] => {
    const { after, before, inc = false, count } = query;

    if (!timezone) {
        // No zone supplied -> native expansion on the FULL ruleset, so EXDATE lines
        // (RRULE + EXDATE) are honored natively (absolute instants for a Z rule).
        const ruleSet: RRuleSet = toRuleSet(rruleString);
        const firstRule = ruleSet._rrule[0];
        let occ: Date[];
        if (before) {
            occ = ruleSet.between(after ?? firstRule.options.dtstart, before, inc);
        } else if (after) {
            const list: Date[] = [];
            let cursor: Date | null = after;
            const limit = count ?? 1;
            for (let i = 0; i < limit; i++) {
                const next: Date | null = ruleSet.after(cursor, i === 0 ? inc : false);
                if (!next) break;
                list.push(next);
                cursor = next;
            }
            occ = list;
        } else {
            occ = count !== undefined ? ruleSet.all((_, i) => i < count) : ruleSet.all();
        }
        return occ;
    }

    // Option B: floating expansion + per-occurrence luxon mapping.
    const parsed = fetchRule(rruleString);
    const floatingRule = toFloatingRule(parsed);
    const untilInstant = getUntilInstant(parsed); // true UTC instant (RFC 5545), not floatified
    const excludedTrueMillis = parseExcludedTrueInstants(rruleString, timezone);

    // Pad the floating search window by ±1 day so no boundary occurrence is lost
    // to a DST shift; the precise real-instant filter below trims the padding.
    const lowerFloat = after
        ? instantToFloatingWallClock(after, timezone, -MS_PER_DAY)
        : floatingRule.options.dtstart;
    // Bound the window by `before`, else by UNTIL, else a default horizon.
    const upperCandidates = [before, untilInstant].filter((d): d is Date => !!d);
    const upperReference = upperCandidates.length
        ? new Date(Math.min(...upperCandidates.map((d) => d.getTime())))
        : new Date((after ?? new Date()).getTime() + DEFAULT_HORIZON_DAYS * MS_PER_DAY);
    const upperFloat = instantToFloatingWallClock(upperReference, timezone, MS_PER_DAY);

    const floatingOccurrences = floatingRule.between(lowerFloat, upperFloat, true);

    const afterMs = after?.getTime();
    const beforeMs = before?.getTime();
    const untilMs = untilInstant?.getTime();

    const seen = new Set<number>();
    const instants = floatingOccurrences
        .map((f) => floatingOccurrenceToInstant(f, timezone))
        .filter((instant) => {
            const t = instant.getTime();
            if (afterMs !== undefined && (inc ? t < afterMs : t <= afterMs)) return false;
            if (beforeMs !== undefined && (inc ? t > beforeMs : t >= beforeMs)) return false;
            if (untilMs !== undefined && t > untilMs) return false; // UNTIL is inclusive (RFC 5545)
            if (excludedTrueMillis.has(t)) return false; // EXDATE
            if (seen.has(t)) return false; // dedupe (e.g. HOURLY spring-forward collision)
            seen.add(t);
            return true;
        })
        .sort((a, b) => a.getTime() - b.getTime());

    return count !== undefined ? instants.slice(0, count) : instants;
};

// rrule walks EVERY occurrence from DTSTART on each after()/between() call, so a SECONDLY/MINUTELY/
// HOURLY series with an old DTSTART costs O(age) per call (a 60-day MINUTELY rule: ~170ms/call, x64
// calls = ~11s of blocked event loop per send confirm). The occurrence grid is DTSTART + k*INTERVAL*unit
// (BYxxx filters only thin that grid), so DTSTART can be moved forward by whole periods without changing
// any occurrence at/after the cursor. Skipped when COUNT is set (it counts from the original DTSTART).
const SUBDAILY_STEP_MS: Record<number, number> = {
    [RRule.SECONDLY]: 1000,
    [RRule.MINUTELY]: MINUTE_IN_MS,
    [RRule.HOURLY]: 60 * MINUTE_IN_MS,
};
const SUBDAILY_FAST_FORWARD_SLACK_MS = 60 * MINUTE_IN_MS;

const fastForwardSubdailyRule = (rule: RRule, cursor: Date): RRule => {
    const options = rule.origOptions;
    const unitMs = options.freq !== undefined ? SUBDAILY_STEP_MS[options.freq] : undefined;
    if (!unitMs || options.count || !options.dtstart) return rule;
    // Day-level BYxxx filters make rrule's phase depend on DTSTART's day (e.g. MINUTELY;INTERVAL=7;BYHOUR
    // is off-grid and shifts with the start day), so moving DTSTART would change real occurrences. Leave
    // those on the exact (slow) path; only BYSECOND/BYMINUTE-style rules are fast-forwarded.
    const o = options as Record<string, unknown>;
    if (['byhour', 'byweekday', 'bynweekday', 'bymonthday', 'bynmonthday', 'bymonth', 'byweekno', 'byyearday', 'bysetpos', 'byeaster']
        .some((k) => o[k] !== undefined && o[k] !== null && !(Array.isArray(o[k]) && (o[k] as unknown[]).length === 0))) return rule;
    const periodMs = unitMs * (options.interval || 1);
    const dtstartMs = options.dtstart.getTime();
    const periods = Math.floor((cursor.getTime() - SUBDAILY_FAST_FORWARD_SLACK_MS - dtstartMs) / periodMs);
    if (periods <= 0) return rule;
    return new RRule({ ...options, dtstart: new Date(dtstartMs + periods * periodMs) });
};

// First real instant after `after` (strict, or inclusive), in-tz. Bounded and
// DST-safe: it collects a small forward buffer of floating occurrences and takes
// the earliest MAPPED instant, so a fall-back/gap reordering cannot mis-pick.
const nextInstantInTz = (rruleString: string, timezone: string, after: Date, inc = false): Date | null => {
    const parsed = fetchRule(rruleString);
    const untilMs = getUntilInstant(parsed)?.getTime();
    const excludedTrueMillis = parseExcludedTrueInstants(rruleString, timezone);
    // Start a touch before the wall-clock of `after` so a boundary occurrence is seen.
    const cursor = instantToFloatingWallClock(after, timezone, -2 * MINUTE_IN_MS);
    const floatingRule = fastForwardSubdailyRule(toFloatingRule(parsed), cursor);
    const buffer: Date[] = [];
    let node: Date | null = cursor;
    for (let i = 0; i < 64; i++) {
        const next: Date | null = floatingRule.after(node, false);
        if (!next) break;
        buffer.push(floatingOccurrenceToInstant(next, timezone));
        node = next;
    }
    const afterMs = after.getTime();
    const candidates = buffer
        .filter((instant) => {
            const t = instant.getTime();
            if (inc ? t < afterMs : t <= afterMs) return false;
            if (untilMs !== undefined && t > untilMs) return false; // UNTIL (RFC 5545, inclusive)
            if (excludedTrueMillis.has(t)) return false; // EXDATE
            return true;
        })
        .sort((a, b) => a.getTime() - b.getTime());
    return candidates.length ? candidates[0] : null;
};

/**
 * Calculates the next due date for a recurring message.
 * 
 * Note: This function maintains LOCAL time consistency during DST transitions.
 * When DST changes occur, the function adjusts the UTC time to keep the same
 * local time by comparing timezone offsets and adjusting accordingly.
 * 
 * Example: A monthly 6 PM message created during winter (GMT+2) will remain at
 * 6 PM even after transitioning to summer time (GMT+3). The offset difference
 * of 60 minutes is subtracted to maintain local time consistency.
 * 
 * This function calculates the next occurrence from the given dueDate,
 * but ensures the result is in the future (after current time).
 *
 * BE#648: when `timezone` (an IANA zone threaded from workspaces.timezone) is
 * supplied, the next occurrence is computed with the runtime-TZ-independent
 * Option B engine so a customer's wall-clock is held across DST regardless of the
 * server's own zone. Omitting `timezone` preserves the legacy behaviour EXACTLY
 * (byte-identical) for un-migrated series.
 */
export const calculateNextDuedate = (dueDate: moment.Moment, message: UserMessage, timezone?: string) => {
    if (!message.isRecurring || !message.rruleset) {
        return null;
    }

    // Option B (tz-aware) path — only when the caller supplies the series' zone.
    if (timezone) {
        try {
            const nowUTC = moment.utc();
            const nextFromDue = nextInstantInTz(message.rruleset, timezone, dueDate.toDate(), false);
            const next = (nextFromDue && moment.utc(nextFromDue).isAfter(nowUTC))
                ? nextFromDue
                : nextInstantInTz(message.rruleset, timezone, nowUTC.toDate(), false);
            return next ? moment.utc(next) : null;
        } catch (error) {
            console.warn('RRULE (tz-aware) parsing failed:', error);
            return null;
        }
    }

    try {
        const ruleSet: RRuleSet = toRuleSet(message.rruleset);
        const parsedRule: RRule = ruleSet._rrule[0];
        const rule = getNormalizedRule(parsedRule, dueDate);
        // EXDATE instants in the rule's own (raw) space; empty for no-EXDATE rules, so
        // getNextAdjustedOccurrence stays byte-identical for un-migrated series.
        const excludedRawMillis = new Set<number>(ruleSet.exdates().map((d) => d.getTime()));
        const dueDateAsDate = dueDate.toDate();
        // Use rule DTSTART offset when available; otherwise fallback to current dueDate offset.
        const offsetRrule = rule?.options?.dtstart?.getTimezoneOffset() ?? dueDateAsDate.getTimezoneOffset();
        const nowUTC = moment.utc();

        // Prefer next occurrence from dueDate; if stale, recalculate from "now" to avoid past results.
        const nextFromDue = getNextAdjustedOccurrence(rule, dueDateAsDate, offsetRrule, excludedRawMillis);
        const nextUTC = (nextFromDue && moment.utc(nextFromDue).isAfter(nowUTC))
            ? nextFromDue
            : getNextAdjustedOccurrence(rule, nowUTC.toDate(), offsetRrule, excludedRawMillis);

        return nextUTC ? moment.utc(nextUTC) : null;
    } catch (error) {
        // If RRULE parsing fails, return null
        console.warn('RRULE parsing failed:', error);
        return null;
    }
}
